import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar, Building2, Mail, Check, Copy, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AdvertisementCategory } from '../types';
import { pbClickTracker } from '../services/pocketbaseClickTracking';
// Removed insecure client-side generation - now using secure server endpoint
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { formatDescriptionHtml } from '../utils/formatDescription';
import { getTranslations, getLocaleFromRegion } from '../utils/translations';

export const PartnerCampaignPreview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get('partner');
  const partnerCampaignId = searchParams.get('campaign');
  
  const { 
    partnerCampaigns, 
    advertisements, 
    partners,
    loading 
  } = useAuth();
  
  const [partnerCampaign, setPartnerCampaign] = useState<any>(null);
  const [partner, setPartner] = useState<any>(null);
  const [showPromoForm, setShowPromoForm] = useState<string | null>(null);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [submittedRequests, setSubmittedRequests] = useState<Set<string>>(new Set());
  const [selectedAdForSession, setSelectedAdForSession] = useState<string | null>(() => {
    return sessionStorage.getItem('selectedAdvertisementId');
  });
  const [selectedCategory, setSelectedCategory] = useState<AdvertisementCategory | null>(null);
  const [generatedPromocode, setGeneratedPromocode] = useState<string | null>(null);
  const [showPromocodeModal, setShowPromocodeModal] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Get locale and translations
  const locale = partnerCampaign?.region ? getLocaleFromRegion(partnerCampaign.region) : 'en';
  const t = getTranslations(locale);

  const availableCategories: AdvertisementCategory[] = [
    'Electronics',
    'Banking',
    'Education',
    'Entertainment',
    'Clothes and footwear',
    'Sport',
    'Beauty and health',
    'Auto',
    'For children',
    'Hotels'
  ];

  useEffect(() => {
    if (partnerId && partnerCampaignId) {
      // Find the specific partner campaign
      const foundCampaign = partnerCampaigns.find(pc => 
        pc.id === partnerCampaignId && pc.partner_id === partnerId
      );
      
      if (foundCampaign) {
        setPartnerCampaign(foundCampaign);
        
        // Find the partner
        const foundPartner = partners.find(p => p.id === partnerId);
        setPartner(foundPartner);
      }
    }
  }, [partnerId, partnerCampaignId, partnerCampaigns, partners]);

  const toggleCategory = (category: AdvertisementCategory) => {
    setSelectedCategory(prev => 
      prev === category ? null : category
    );
  };

  const handlePromoRequest = async (partnerCampaignId: string, partnerId: string) => {
    if (!email.trim()) {
      alert('Please enter your email address');
      return;
    }
    if (!agreedToPrivacy) {
      alert('Please agree to the Privacy Policy');
      return;
    }
    
    if (email && agreedToPrivacy && selectedAd) {
      // Request promocode from secure server endpoint
      console.log('📤 Requesting promocode from secure endpoint...');
      
      try {
        const { data: promoResponse, error: promoError } = await DataService.requestPromocode(
          selectedAd.id,
          email,
          agreedToPrivacy
        );
        
        if (promoError) {
          console.error('❌ Promocode request failed:', promoError);
          alert(promoError);
          setShowPromoForm(null);
          return;
        }
        
        console.log('✅ Promocode response:', promoResponse);
        
        // Handle redirect response
        if (promoResponse?.redirect_url) {
          // Store selected advertisement in session
          setSelectedAdForSession(selectedAd.id);
          sessionStorage.setItem('selectedAdvertisementId', selectedAd.id);
          sessionStorage.setItem('selectedAdvertisementData', JSON.stringify({
            advertisementId: selectedAd.id,
            advertisementName: selectedAd.name,
            partnerCampaignId: partnerCampaignId,
            partnerId: partnerId,
            email: email,
            promocode: 'REDIRECT',
            submittedAt: new Date().toISOString()
          }));

          // Track promo request
          await pbClickTracker.trackPromoRequest(
            partnerId,
            partnerCampaignId,
            selectedAd.id,
            email,
            partnerCampaignId
          );

          setSubmittedRequests(prev => new Set([...prev, partnerCampaignId]));
          setShowPromoForm(null);

          // Redirect
          window.location.href = promoResponse.redirect_url;
          return;
        }
        
        // Handle promocode response
        const promocode = promoResponse?.promo_code || promoResponse?.message || '';
        console.log('✅ Received promocode:', promocode);
      
        // Store selected advertisement in session
        setSelectedAdForSession(selectedAd.id);
        sessionStorage.setItem('selectedAdvertisementId', selectedAd.id);
        sessionStorage.setItem('selectedAdvertisementData', JSON.stringify({
          advertisementId: selectedAd.id,
          advertisementName: selectedAd.name,
          partnerCampaignId: partnerCampaignId,
          partnerId: partnerId,
          email: email,
          promocode: promocode,
          submittedAt: new Date().toISOString()
        }));
      
        // Track promo request
        await pbClickTracker.trackPromoRequest(
          partnerId,
          partnerCampaignId,
          selectedAd.id,
          email,
          partnerCampaignId
        );
        
        console.log('🎫 Final promocode to display:', promocode || 'EMPTY');
        
        // Note: The server endpoint already created the promo_request record
        // We just need to update UI state
        setSubmittedRequests(prev => new Set([...prev, partnerCampaignId]));
        setShowPromoForm(null);
        
        // Show promocode modal
        if (promocode) {
          setGeneratedPromocode(promocode);
          setShowPromocodeModal(true);
        } else {
          alert('Request submitted successfully! You can only select one advertisement per session.');
        }
        
        setSelectedAd(null);
        setEmail('');
        setAgreedToPrivacy(false);
      } catch (error: any) {
        console.error('❌ Failed to request promocode:', error);
        alert(error.message || 'Failed to request promocode. Please try again.');
        setShowPromoForm(null);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    });
  };

  const getCampaignAds = () => {
    if (!partnerCampaign) return [];
    
    const ads = advertisements.filter(ad => 
      partnerCampaign.advertisement_ids.includes(ad.id) && 
      ad.status === 'active' &&
      (!ad.end_date || ad.end_date === '' || new Date(ad.end_date) >= new Date()) &&
      ad.region === partnerCampaign.region
    );
    
    // Sort by priority: platinum > gold > silver > bronze
    const priorityOrder: Record<string, number> = { platinum: 1, gold: 2, silver: 3, bronze: 4 };
    return ads.sort((a, b) => (priorityOrder[a.priority] || 5) - (priorityOrder[b.priority] || 5));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (!partnerCampaign || !partner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl text-gray-300 mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Campaign Not Found</h1>
          <p className="text-gray-600">
            The campaign you're looking for doesn't exist or is no longer active.
          </p>
        </div>
      </div>
    );
  }

  const allAds = getCampaignAds();
  const filteredAds = selectedCategory 
    ? allAds.filter(ad => ad.categories && ad.categories.includes(selectedCategory))
    : allAds;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {partner.logo_url ? (
                <img 
                  src={partner.logo_url} 
                  alt={partner.name}
                  className="h-10 w-10 rounded-lg object-cover"
                />
              ) : (
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
                  <Building2 className="text-white" size={24} />
                </div>
              )}
              <div className="ml-3">
                <h1 className="text-xl font-semibold text-gray-800">{partner.name}</h1>
                <p className="text-sm text-gray-500">{partnerCampaign.name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 min-h-screen">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{partnerCampaign.name}</h2>
          <p className="text-gray-600">
            {partnerCampaign.description || 'Discover amazing deals and offers'}
          </p>
        </div>

        {/* Category Filters - Always visible */}
        {availableCategories.length > 0 && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter by Categories</h3>
              <div className="flex flex-wrap gap-2">
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
                {selectedCategory && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 ml-2"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {filteredAds.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl text-gray-300 mb-4">📢</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Offers</h2>
            <p className="text-gray-600">
              {selectedCategory 
                ? `No offers found in the "${selectedCategory}" category. Try clearing the filter.`
                : 'This campaign currently has no active offers to display.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredAds.map((ad) => (
                <div 
                  key={ad.id} 
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1 cursor-pointer ${
                    selectedAdForSession && selectedAdForSession !== ad.id ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={async () => {
                    if (!selectedAdForSession || selectedAdForSession === ad.id) {
                      setSelectedAd({ ...ad, partnerCampaign, partner });
                      
                      // Track advertisement view
                      await pbClickTracker.trackAdvertisementView(
                        partnerId!,
                        partnerCampaignId!,
                        ad.id,
                        partnerCampaignId!,
                        {
                          campaign_name: partnerCampaign.name,
                          advertisement_name: ad.name,
                          partner_name: partner.name
                        }
                      );
                    }
                  }}
                >
                  <div className="p-1.5">
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-3">
                      {ad.banner_url ? (
                        <img 
                          src={ad.banner_url} 
                          alt={ad.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                          <Building2 className="text-purple-600" size={48} />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="text-base font-semibold text-gray-900 text-center mb-3 px-1">{ad.name}</h3>
                    <button
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2.5 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 font-medium text-sm"
                    >
                      Get the Offer
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left text-gray-500 mb-4 md:mb-0">
              <p className="text-sm">
                Powered by {partner.name}
              </p>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800">Terms of Service</Link>
              <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>
              <Link to="/contact-us" className="text-blue-600 hover:text-blue-800">Contact Us</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Advertisement Modal */}
      {selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="aspect-video bg-gray-100 relative overflow-hidden p-4">
              <img
                src={selectedAd.banner_url}
                alt={selectedAd.name}
                className="w-full h-full object-cover rounded-lg"
              />
              <button
                onClick={() => setSelectedAd(null)}
                className="absolute top-6 right-6 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition duration-200"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{selectedAd.name}</h2>
                {partner.logo_url && (
                  <img 
                    src={partner.logo_url} 
                    alt={partner.name}
                    className="w-16 h-16 rounded object-cover"
                  />
                )}
              </div>
              
              <div
                className="text-gray-600 mb-6 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formatDescriptionHtml(selectedAd.description) }}
              />
              
              {selectedAd.categories && selectedAd.categories.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Categories:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedAd.categories.map((category: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center text-sm text-gray-500 mb-6">
                <Calendar size={16} className="mr-2" />
                <span>Valid until {new Date(selectedAd.end_date).toLocaleDateString()}</span>
              </div>
              
              {submittedRequests.has(partnerCampaignId!) ? (
                <div className="w-full bg-green-100 text-green-800 py-3 px-4 rounded-lg flex items-center justify-center font-medium">
                  <Check size={18} className="mr-2" />
                  {t.promoCodeRequested}
                </div>
              ) : showPromoForm === partnerCampaignId ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div className="flex items-start space-x-3 py-2">
                    <input
                      type="checkbox"
                      checked={agreedToPrivacy}
                      onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-600">
                      I agree with <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>
                    </label>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowPromoForm(null);
                        setEmail('');
                        setAgreedToPrivacy(false);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handlePromoRequest(partnerCampaignId!, partnerId!)}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                    >
                      Request Code
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={() => setShowPromoForm(partnerCampaignId!)}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium"
                >
                  <Mail size={18} className="mr-2" />
                  Get Promo Code
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Promocode Modal */}
      {showPromocodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => {
                setShowPromocodeModal(false);
                setGeneratedPromocode(null);
                setCopiedToClipboard(false);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              ✕
            </button>

            {generatedPromocode === 'API_PENDING' ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">API Request Sent</h3>
                <p className="text-gray-600 mb-6">
                  Your promocode request has been sent. You will receive your code via email shortly.
                </p>
                <button
                  onClick={() => {
                    setShowPromocodeModal(false);
                    setGeneratedPromocode(null);
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-6 rounded-lg hover:from-orange-600 hover:to-red-700 transition duration-200 font-medium"
                >
                  Got it!
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 Your Promocode!</h3>
                <p className="text-gray-600 mb-6">
                  Here's your exclusive promocode. Copy it and use it at checkout!
                </p>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <div className="text-sm text-gray-600 mb-2">Your Promocode:</div>
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4 font-mono tracking-wider">
                    {generatedPromocode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generatedPromocode!)}
                    className={`flex items-center justify-center space-x-2 mx-auto px-4 py-2 rounded-lg transition duration-200 ${
                      copiedToClipboard
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {copiedToClipboard ? (
                      <>
                        <Check size={18} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={18} />
                        <span>Copy Code</span>
                      </>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => {
                    setShowPromocodeModal(false);
                    setGeneratedPromocode(null);
                    setCopiedToClipboard(false);
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
